import React, { useEffect, useRef } from "react";
import { HTMLContent } from "./Content";

const MERMAID_SCRIPT_ID = "mermaid-cdn-script";
const MERMAID_SCRIPT_SRC =
  "https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js";

const MERMAID_BRAND_THEME = {
  theme: "base",
  darkMode: true,
  themeVariables: {
    background: "#221E44",
    primaryColor: "#3C2D89",
    primaryBorderColor: "#49509D",
    primaryTextColor: "#FFFFFF",
    secondaryColor: "#412682",
    secondaryBorderColor: "#49509D",
    secondaryTextColor: "#FFFFFF",
    tertiaryColor: "#312760",
    tertiaryBorderColor: "#49509D",
    tertiaryTextColor: "#FFFFFF",
    lineColor: "#A8B4FF",
    textColor: "#FFFFFF",
    nodeTextColor: "#FFFFFF",
    edgeLabelBackground: "#221E44",
    clusterBkg: "#2A2553",
    clusterBorder: "#6A70B2",
    titleColor: "#FFFFFF",
    fontFamily: "Inter, Segoe UI, sans-serif",
  },
  flowchart: {
    htmlLabels: true,
    useMaxWidth: true,
    curve: "basis",
  },
  sequence: {
    useMaxWidth: true,
  },
  gantt: {
    useMaxWidth: true,
  },
};

const MERMAID_START_TOKENS = [
  "graph ",
  "flowchart ",
  "sequenceDiagram",
  "classDiagram",
  "stateDiagram",
  "erDiagram",
  "journey",
  "gantt",
  "pie",
  "mindmap",
  "timeline",
  "quadrantChart",
  "gitGraph",
  "C4Context",
  "C4Container",
  "C4Component",
  "C4Dynamic",
  "C4Deployment",
];

const normalizeLanguageHints = (node) => {
  if (!node) {
    return "";
  }

  const className = typeof node.className === "string" ? node.className : "";
  const dataLanguage =
    typeof node.getAttribute === "function"
      ? node.getAttribute("data-language") || ""
      : "";

  return `${className} ${dataLanguage}`.toLowerCase();
};

const looksLikeMermaid = (source) => {
  if (!source) {
    return false;
  }

  const normalized = source.trim();
  return MERMAID_START_TOKENS.some((token) => normalized.startsWith(token));
};

const extractDeckgoMermaidSource = (deckgoBlock) => {
  if (!deckgoBlock) {
    return "";
  }

  const directSlotCode = deckgoBlock.querySelector('code[slot="code"]');
  if (directSlotCode && directSlotCode.textContent) {
    return directSlotCode.textContent.trim();
  }

  const fallbackText = deckgoBlock.textContent || "";
  return fallbackText.trim();
};

const loadMermaidScript = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      resolve(null);
      return;
    }

    if (window.mermaid) {
      resolve(window.mermaid);
      return;
    }

    const existingScript = document.getElementById(MERMAID_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.mermaid));
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = MERMAID_SCRIPT_ID;
    script.src = MERMAID_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.mermaid);
    script.onerror = reject;
    document.body.appendChild(script);
  });

const MermaidContent = ({ content, className }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !containerRef.current || !content) {
      return;
    }

    let isMounted = true;

    const renderMermaidDiagrams = async () => {
      try {
        const mermaid = await loadMermaidScript();
        if (!isMounted || !mermaid) {
          return;
        }

        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "loose",
          maxTextSize: 120000,
          ...MERMAID_BRAND_THEME,
        });

        const deckgoBlocks = containerRef.current.querySelectorAll(
          'deckgo-highlight-code[language="mermaid"]',
        );

        deckgoBlocks.forEach((deckgoBlock) => {
          const source = extractDeckgoMermaidSource(deckgoBlock);
          if (!looksLikeMermaid(source)) {
            return;
          }

          const mermaidBlock = document.createElement("div");
          mermaidBlock.className = "mermaid";
          mermaidBlock.textContent = source;
          deckgoBlock.replaceWith(mermaidBlock);
        });

        const codeBlocks = containerRef.current.querySelectorAll("pre code");

        codeBlocks.forEach((codeBlock) => {
          const preBlock = codeBlock.closest("pre");
          if (!preBlock) {
            return;
          }

          const highlightWrapper =
            preBlock.closest(".gatsby-highlight-code") ||
            preBlock.closest(".gatsby-highlight") ||
            preBlock.closest("[data-language]");

          const languageHints = [
            normalizeLanguageHints(codeBlock),
            normalizeLanguageHints(preBlock),
            normalizeLanguageHints(highlightWrapper),
          ].join(" ");

          const source = (codeBlock.textContent || "").trim();
          const isMermaidBlock =
            languageHints.includes("mermaid") || looksLikeMermaid(source);

          if (!isMermaidBlock || !source) {
            return;
          }

          const mermaidBlock = document.createElement("div");
          mermaidBlock.className = "mermaid";
          mermaidBlock.textContent = source;

          const replaceTarget = highlightWrapper || preBlock;
          replaceTarget.replaceWith(mermaidBlock);
        });

        const diagrams = containerRef.current.querySelectorAll(".mermaid");
        if (diagrams.length > 0) {
          mermaid.init(undefined, diagrams);
        }
      } catch (error) {
        // Keep the original code block visible if Mermaid fails to load.
      }
    };

    renderMermaidDiagrams();

    return () => {
      isMounted = false;
    };
  }, [content]);

  return (
    <div ref={containerRef}>
      <HTMLContent content={content} className={className} />
    </div>
  );
};

export default MermaidContent;
